import React, { useState } from "react";
import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Toaster } from "react-hot-toast";
import { notifyError, notifySuccess } from "../../../components/AppToast";
import { AddToCalendar } from "../../../components/AddToCalendar";
import { ContinuityPanel } from "../components/ContinuityPanel";
import { CreateClassModal } from "../components/CreateClassModal";
import { formatTime12h } from "../../../utils/formatTime";
import { usePlanning, useUpdatePlanning } from "../hooks/usePlannings";
import { useContinuity } from "../hooks/useContinuity";

// Backend status: "active" = fecha futura, "draft" = fecha ya pasada.
const STATUS_LABEL = {
  active: "Programada",
  cancelled: "Cancelada",
  draft: "Finalizada",
};

function ClassDetailPage() {
  const { slug, planningId } = useParams();
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);

  const { data: planning, isLoading, error } = usePlanning(planningId);
  const updateMutation = useUpdatePlanning(planningId);
  const {
    data: continuity,
    isLoading: continuityLoading,
    error: continuityError,
  } = useContinuity(planningId);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error || !planning) {
    return (
      <div className="container-fluid px-3 py-3">
        <Alert variant="danger">No se pudo cargar la clase.</Alert>
        <Link to={`/${slug}/app/agenda`} className="btn btn-light" style={{ minHeight: 44 }}>
          ← Volver a agenda
        </Link>
      </div>
    );
  }

  const topics = planning.topics ?? [];
  const finalized = planning.finalized === true;

  const handleSave = (data) => {
    updateMutation.mutate(data, {
      onSuccess: (res) => {
        const n = res?._notified ?? 0;
        notifySuccess(
          n > 0
            ? `Clase actualizada — ${n} estudiante(s) notificado(s).`
            : "Clase actualizada."
        );
        setShowEdit(false);
      },
      onError: (err) => {
        notifyError(
          err?.response?.data?.detail ?? "No se pudo actualizar la clase."
        );
      },
    });
  };

  return (
    <div className="container-fluid px-3">
        <Toaster position="top-right" />
        <div className="py-3 d-flex justify-content-between align-items-start gap-2">
          <div>
            <Link
              to={`/${slug}/app/agenda`}
              className="text-muted small d-block mb-2"
            >
              ← Agenda
            </Link>
            <h1 className="h4 mb-0">
              {topics.length > 0 ? topics.map((t) => t.name ?? t).join(", ") : "Clase"}
            </h1>
            <p className="text-muted mb-0">
              {dayjs(planning.date).format("dddd D [de] MMMM")}
              {planning.place ? ` · ${planning.place}` : ""}
            </p>
          </div>
          <div className="d-flex flex-column flex-sm-row gap-2 flex-shrink-0">
            <AddToCalendar
              title={
                topics.length > 0
                  ? topics.map((t) => t.name ?? t).join(", ")
                  : "Clase"
              }
              date={planning.date}
              timeStart={planning.time_start}
              duration={planning.duration}
              location={[planning.place, planning.classroom?.name]
                .filter(Boolean)
                .join(" · ")}
              description={
                planning.description &&
                planning.description !== "Clase programada"
                  ? planning.description
                  : ""
              }
              uid={`clase-${planningId}`}
              size="sm"
            />
            {!finalized && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowEdit(true)}
                style={{ minHeight: 40 }}
              >
                <i className="mdi mdi-pencil-outline me-1" />
                Editar
              </Button>
            )}
          </div>
        </div>

        {/* Datos de la clase */}
        <Card className="mb-3">
          <Card.Body>
            <div className="row g-2">
              {planning.time_start && (
                <div className="col-6">
                  <small className="text-muted d-block">Hora</small>
                  <strong>{formatTime12h(planning.time_start)}</strong>
                </div>
              )}
              {planning.duration && (
                <div className="col-6">
                  <small className="text-muted d-block">Duración</small>
                  <strong>{planning.duration}</strong>
                </div>
              )}
              <div className="col-6">
                <small className="text-muted d-block">Inscritos</small>
                <strong>{planning.enrolled_count ?? "—"}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Estado</small>
                <Badge bg={planning.status === "active" ? "success" : "secondary"}>
                  {STATUS_LABEL[planning.status] ?? "Finalizada"}
                </Badge>
              </div>
              {planning.classroom && (
                <div className="col-6">
                  <small className="text-muted d-block">Aula</small>
                  <strong>{planning.classroom.name}</strong>
                </div>
              )}
            </div>
            {planning.description && (
              <p className="mt-2 mb-0 text-muted small">{planning.description}</p>
            )}
          </Card.Body>
        </Card>

        {/* Panel de continuidad */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Continuidad por tema</strong>
          </Card.Header>
          <Card.Body>
            <ContinuityPanel
              data={continuity}
              isLoading={continuityLoading}
              error={continuityError}
            />
          </Card.Body>
        </Card>

        {/* Acciones — asistencia y calificación en una sola pantalla */}
        <div className="d-flex flex-column gap-2 pb-4">
          <Button
            variant="primary"
            onClick={() => navigate(`/${slug}/app/agenda/${planningId}/grade-card`)}
            style={{ minHeight: 56 }}
          >
            ✓ Asistencia y calificación
          </Button>
        </div>

        <CreateClassModal
          show={showEdit}
          onHide={() => setShowEdit(false)}
          onCreate={handleSave}
          isLoading={updateMutation.isPending}
          error={updateMutation.error}
          mode="edit"
          initial={planning}
        />
    </div>
  );
}

export { ClassDetailPage };
